import React, { useEffect, Suspense, lazy } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";

import Navbar from "./components/Navbar";
import MobileTabBar from "./components/MobileTabBar";
import ErrorBoundary from "./components/ErrorBoundary";
import WelcomePage from "./pages/WelcomePage";
import { getStoredUser, hasActiveSession } from "./utils/authStorage";

// ✅ Pages (lazy-loaded for performance)
const HomePage = lazy(() => import("./pages/HomePage"));
const SpeciesList = lazy(() => import("./pages/SpeciesList"));
const SpeciesDetails = lazy(() => import("./pages/SpeciesDetails"));
const MapView = lazy(() => import("./pages/MapView"));
const AwarenessHub = lazy(() => import("./pages/AwarenessHub"));
const NationalParksPage = lazy(() => import("./pages/NationalParksPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const UserLoginPage = lazy(() => import("./pages/UserLoginPage"));
const OAuthCallback = lazy(() => import("./pages/OAuthCallback.jsx"));
const AdminPortal = lazy(() => import("./pages/AdminPortal"));
const AddSpeciesForm = lazy(() => import("./pages/AddSpeciesForm"));
const EditSpeciesForm = lazy(() => import("./pages/EditSpeciesForm"));
const ToolsPage = lazy(() => import("./pages/ToolsPage"));
const DatasetsPage = lazy(() => import("./pages/DatasetsPage"));
const ContributeDatasetPage = lazy(() => import("./pages/ContributeDatasetPage"));
const ReportsCenter = lazy(() => import("./pages/ReportsCenter"));
const AIDetect = lazy(() => import("./pages/AIDetect.jsx"));
const PowerBIPage = lazy(() => import("./pages/PowerBIPage.jsx"));
const ReportSighting = lazy(() => import("./pages/ReportSighting.jsx"));

function ProtectedRoute({ children, notice, adminOnly = false }) {
  const location = useLocation();

  if (!hasActiveSession()) {
    return (
      <Navigate
        to="/user-login"
        replace
        state={{
          authNotice:
            notice ||
            "Please login first. DSS and Report features are available only after login.",
          from: location.pathname,
        }}
      />
    );
  }

  if (adminOnly) {
    const role = String(getStoredUser()?.role || "").toLowerCase();
    if (role !== "admin") {
      return (
        <Navigate
          to="/login"
          replace
          state={{
            authNotice: "Admin login is required to access this page.",
            from: location.pathname,
          }}
        />
      );
    }
  }

  return children;
}

export default function App() {
  const location = useLocation();
  const authPaths = ["/", "/login", "/user-login", "/oauth/callback"];
  const isLoginLanding = location.pathname === "/login" || location.pathname === "/user-login";
  const isAuthScreen = isLoginLanding || authPaths.includes(location.pathname);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.classList.toggle("auth-mode", isAuthScreen);
  }, [isAuthScreen]);

  return (
    <div className={`app-shell${isAuthScreen ? " auth-full" : ""}`}>
      {!isAuthScreen ? <Navbar /> : null}
      <main className={`app-main${isAuthScreen ? " auth-main" : ""}`}>
        <Suspense fallback={<div className="center">Loading...</div>}>
          <Routes>

          {/* MAIN ROUTES */}
          <Route path="/" element={<WelcomePage />} />
          <Route
            path="/home"
            element={
              <ErrorBoundary>
                <HomePage />
              </ErrorBoundary>
            }
          />
          <Route path="/species" element={<SpeciesList />} />
          <Route path="/species/:id" element={<SpeciesDetails />} />
          <Route path="/map" element={<MapView />} />
          <Route path="/awareness" element={<AwarenessHub />} />
          <Route path="/parks" element={<NationalParksPage />} />
          <Route path="/about" element={<AboutPage />} />

          {/* TOOLS / AI / DATA */}
          <Route path="/ai-detect" element={<AIDetect />} />
          <Route path="/analytics" element={<PowerBIPage />} />
          <Route
            path="/report"
            element={
              <ProtectedRoute>
                <ReportSighting />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <ErrorBoundary>
                  <ReportsCenter />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route path="/datasets" element={<DatasetsPage />} />
          <Route path="/contact" element={<ContributeDatasetPage />} />
          <Route path="/tools" element={<ToolsPage />} />

          {/* ADMIN */}
          <Route
            path="/admin"
            element={
              <AdminPortal />
            }
          />
          <Route
            path="/add-species"
            element={
              <ProtectedRoute adminOnly>
                <AddSpeciesForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/edit-species/:id"
            element={
              <ProtectedRoute adminOnly>
                <EditSpeciesForm />
              </ProtectedRoute>
            }
          />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/user-login" element={<UserLoginPage />} />
          <Route path="/oauth/callback" element={<OAuthCallback />} />

          </Routes>
        </Suspense>
      </main>
      {!isAuthScreen ? <MobileTabBar /> : null}
    </div>
  );
}
